import { ImportTransformType as T } from './import-definition.js'

/**
 * The transform catalogue (TO-400 BR-14): every declarative transform with
 * exactly the params the F5 contract checker accepts for it, described so a UI
 * can render inputs. A parity test in file-exchange-service compares the names
 * against the checker's param table both ways — add a param there and here.
 */

export type ImportTransformParamSpec =
	| { name: string; kind: 'integer'; min: number; max: number; nullable?: boolean }
	| { name: string; kind: 'text'; min: number; max: number }
	| { name: string; kind: 'number'; nonZero?: boolean }
	| { name: string; kind: 'enum'; values: readonly string[] }
	/** A string (≤ 1 000 code points), a finite number or a boolean. */
	| { name: string; kind: 'scalar' }
	/** An object of 1–1 000 source → scalar entries. */
	| { name: string; kind: 'lookup' }
	/** An RE2 pattern. */
	| { name: string; kind: 'regex' }
	/** An IANA time zone. */
	| { name: string; kind: 'timezone' }
	/** `{source}` placeholders over the mapping item's sources. */
	| { name: string; kind: 'template' }

export interface ImportTransformDescriptor {
	label: string
	/** A composition transform combines several sources and may only come first. */
	composition: boolean
	params: readonly ImportTransformParamSpec[]
}

const pad: readonly ImportTransformParamSpec[] = [
	{ name: 'length', kind: 'integer', min: 1, max: 10_000 },
	{ name: 'value', kind: 'text', min: 1, max: 1 },
]
const dateFormat: ImportTransformParamSpec = { name: 'format', kind: 'text', min: 1, max: 64 }

const plain = (label: string, params: readonly ImportTransformParamSpec[] = []): ImportTransformDescriptor => ({
	label,
	composition: false,
	params,
})

export const IMPORT_TRANSFORM_CATALOGUE: Readonly<Record<T, ImportTransformDescriptor>> = Object.freeze({
	[T.TRIM]: plain('Trim'),
	[T.UPPERCASE]: plain('Uppercase'),
	[T.LOWERCASE]: plain('Lowercase'),
	[T.SUBSTRING]: plain('Substring', [
		{ name: 'start', kind: 'integer', min: 0, max: 10_000 },
		{ name: 'length', kind: 'integer', min: 1, max: 10_000, nullable: true },
	]),
	[T.REPLACE]: plain('Replace', [
		{ name: 'search', kind: 'text', min: 1, max: 255 },
		{ name: 'replacement', kind: 'text', min: 0, max: 255 },
	]),
	[T.REGEX_REPLACE]: plain('Regex replace', [
		{ name: 'pattern', kind: 'regex' },
		{ name: 'replacement', kind: 'text', min: 0, max: 255 },
	]),
	[T.PAD_START]: plain('Pad start', pad),
	[T.PAD_END]: plain('Pad end', pad),
	[T.EMPTY_TO_NULL]: plain('Empty to null'),
	[T.TO_STRING]: plain('To text'),
	[T.TO_INTEGER]: plain('To integer'),
	[T.TO_DECIMAL]: plain('To decimal', [{ name: 'decimalSeparator', kind: 'enum', values: ['.', ','] }]),
	[T.TO_BOOLEAN]: plain('To boolean'),
	[T.PARSE_DATE]: plain('Parse date', [dateFormat]),
	[T.PARSE_DATE_TIME]: plain('Parse date-time', [dateFormat, { name: 'timezone', kind: 'timezone' }]),
	[T.FORMAT_DATE]: plain('Format date', [dateFormat]),
	[T.LOOKUP]: plain('Lookup', [{ name: 'values', kind: 'lookup' }]),
	[T.DEFAULT]: plain('Default', [{ name: 'value', kind: 'scalar' }]),
	[T.COALESCE]: { label: 'First non-blank', composition: true, params: [] },
	[T.MULTIPLY]: plain('Multiply', [{ name: 'by', kind: 'number' }]),
	[T.DIVIDE]: plain('Divide', [{ name: 'by', kind: 'number', nonZero: true }]),
	[T.ROUND]: plain('Round', [{ name: 'decimals', kind: 'integer', min: 0, max: 10 }]),
	[T.CONCAT]: {
		label: 'Concatenate',
		composition: true,
		params: [{ name: 'separator', kind: 'text', min: 0, max: 255 }],
	},
	[T.TEMPLATE]: { label: 'Template', composition: true, params: [{ name: 'template', kind: 'template' }] },
})
